/**
 * Side pot calculation utilities
 * Pure functions for calculating pots from player bets
 *
 * PotStructure is now a flat Pot[] array sorted by cumulativeLevel ascending.
 * The first pot (index 0) is the main pot (lowest cumulativeLevel).
 */

import type { EnginePlayer, Pot, PotStructure, PotDistribution, Card } from './types'
import { findWinnersForPot } from './evaluator'

// ============================================================================
// HELPER FUNCTIONS - Convenience utilities for working with pot structures
// ============================================================================

/**
 * Get all pots as a flat array
 * With the new structure, this is just an identity function for backward compat
 *
 * @param potStructure - The pot structure (Pot[])
 * @returns The same array
 */
export function getAllPots(potStructure: PotStructure): Pot[] {
  return potStructure
}

/**
 * Get the main pot (first pot with lowest cumulativeLevel)
 *
 * @param potStructure - The pot structure (Pot[])
 * @returns The main pot, or undefined if empty
 */
export function getMainPot(potStructure: PotStructure): Pot | undefined {
  return potStructure[0]
}

/**
 * Get side pots (all pots except the main pot)
 *
 * @param potStructure - The pot structure (Pot[])
 * @returns Array of side pots (empty if only main pot exists)
 */
export function getSidePots(potStructure: PotStructure): Pot[] {
  return potStructure.slice(1)
}

/**
 * Get the cumulative contributions for all players across all pots
 * Useful for understanding total player investment
 *
 * @param potStructure - The pot structure to analyze
 * @returns Map of playerId to total contribution
 *
 * @example
 * ```typescript
 * const contributions = getCumulativeContributions(pots)
 * // Map { 'player-1' => 150, 'player-2' => 100, 'player-3' => 150 }
 * ```
 */
export function getCumulativeContributions(potStructure: PotStructure): Map<string, number> {
  const cumulative = new Map<string, number>()

  for (const pot of potStructure) {
    for (const [playerId, amount] of Object.entries(pot.contributions)) {
      cumulative.set(playerId, (cumulative.get(playerId) || 0) + amount)
    }
  }

  return cumulative
}

/**
 * Get a specific player's total contribution across all pots
 *
 * @param potStructure - The pot structure to search
 * @param playerId - The player ID to look up
 * @returns Total chips contributed by this player
 */
export function getPlayerTotalContribution(potStructure: PotStructure, playerId: string): number {
  return getCumulativeContributions(potStructure).get(playerId) || 0
}

/**
 * Check if any side pots exist (more than one pot)
 *
 * @param potStructure - The pot structure to check
 * @returns true if there are side pots (length > 1)
 */
export function hasSidePots(potStructure: PotStructure): boolean {
  return potStructure.length > 1
}

/**
 * Get the total number of pots
 *
 * @param potStructure - The pot structure to count
 * @returns Total pot count
 */
export function getPotCount(potStructure: PotStructure): number {
  return potStructure.length
}

/**
 * Extract uncalled bets from a pot structure.
 *
 * In real poker, when a player bets more than anyone else can match, the
 * excess is returned immediately ("uncalled bet"). The engine represents
 * these as pots with a single eligible player. This function removes them
 * and returns the amounts to be credited back to each player.
 *
 * @param potStructure - The pot structure to process
 * @returns Object with cleaned pot structure and a record of playerId → chips to return
 */
export function extractUncalledBets(potStructure: PotStructure): {
  pots: PotStructure
  uncalledBets: Record<string, number>
} {
  const uncalledBets: Record<string, number> = {}

  const cleaned = potStructure.filter((pot) => {
    if (pot.eligiblePlayerIds.length === 1 && pot.amount > 0) {
      const playerId = pot.eligiblePlayerIds[0]
      uncalledBets[playerId] = (uncalledBets[playerId] || 0) + pot.amount
      return false
    }
    return true
  })

  return {
    pots: cleaned.length > 0 ? cleaned : createEmptyPotStructure(),
    uncalledBets
  }
}

/**
 * Create an empty pot structure
 * Returns a single pot with zero amount (main pot placeholder)
 *
 * @returns Empty PotStructure with single zero-amount pot
 */
export function createEmptyPotStructure(): PotStructure {
  return [
    {
      amount: 0,
      eligiblePlayerIds: [],
      contributions: {},
      cumulativeLevel: 0
    }
  ]
}

/**
 * Create a single pot with all players (no side pots)
 * Convenience helper for when there are no all-ins
 */
function createSinglePot(
  amount: number,
  eligiblePlayerIds: string[],
  contributions: Record<string, number>
): PotStructure {
  return [
    {
      amount,
      eligiblePlayerIds,
      contributions,
      cumulativeLevel: 0
    }
  ]
}

// ============================================================================
// INTERNAL HELPERS - Extracted from mergePotStructures for clarity
// ============================================================================

/**
 * Collect cumulative contributions from a pot structure into a record
 * @internal
 */
function collectContributionsFromStructure(potStructure: PotStructure, target: Record<string, number>): void {
  for (const pot of potStructure) {
    for (const [playerId, amount] of Object.entries(pot.contributions)) {
      target[playerId] = (target[playerId] || 0) + amount
    }
  }
}

/**
 * Determine all-in levels from player states
 * @internal
 */
function determineAllInLevels(
  players: EnginePlayer[] | undefined,
  cumulativeContributions: Record<string, number>,
  existingPots: PotStructure,
  newRoundPots: PotStructure
): { allInPlayerIds: Set<string>; allInLevels: Set<number> } {
  const allInPlayerIds = new Set<string>()
  const allInLevels = new Set<number>()

  if (players) {
    for (const player of players) {
      if (player.state === 'allin') {
        allInPlayerIds.add(player.id)
        const playerContribution = cumulativeContributions[player.id]
        if (playerContribution !== undefined) {
          allInLevels.add(playerContribution)
        }
      }
    }
  } else {
    // Fallback: check if existing or new structure has side pots (indicating all-ins occurred)
    const hasSidePotsInEither = existingPots.length > 1 || newRoundPots.length > 1
    if (hasSidePotsInEither) {
      // Use all unique contribution levels as potential all-in boundaries
      for (const amount of Object.values(cumulativeContributions)) {
        allInLevels.add(amount)
      }
    }
  }

  return { allInPlayerIds, allInLevels }
}

/**
 * Build the set of eligible player IDs based on current round state
 * @internal
 */
function buildEligibilitySet(
  players: EnginePlayer[] | undefined,
  newRoundPots: PotStructure,
  existingPots: PotStructure,
  cumulativeContributions: Record<string, number>
): Set<string> {
  // Start with new round's eligibility (most current, reflects folds)
  const newRoundEligible = new Set<string>(newRoundPots.flatMap((p) => p.eligiblePlayerIds))

  const currentEligibleSet = new Set<string>(newRoundEligible)

  // Add all-in players from previous rounds who are still eligible
  if (players) {
    for (const player of players) {
      if (player.state === 'allin') {
        const wasEligible = existingPots.some((p) => p.eligiblePlayerIds.includes(player.id))
        if (wasEligible) {
          currentEligibleSet.add(player.id)
        }
      }
    }
  } else {
    // Fallback: assume players in existing pots but not in new round are all-in
    for (const pot of existingPots) {
      for (const playerId of pot.eligiblePlayerIds) {
        if (cumulativeContributions[playerId] !== undefined && !newRoundEligible.has(playerId)) {
          if (pot.contributions[playerId] !== undefined) {
            currentEligibleSet.add(playerId)
          }
        }
      }
    }
  }

  return currentEligibleSet
}

/**
 * Build pots from contribution levels with equal contributions per pot
 * @internal
 */
function buildPotsFromLevels(
  levels: number[],
  cumulativeContributions: Record<string, number>,
  eligibleSet: Set<string>
): Pot[] {
  const pots: Pot[] = []
  let previousLevel = 0

  for (const level of levels) {
    const amountPerPlayer = level - previousLevel

    if (amountPerPlayer <= 0) {
      previousLevel = level
      continue
    }

    // Players who contributed at least up to this level
    const contributingPlayerIds = Object.entries(cumulativeContributions)
      .filter(([, total]) => total >= level)
      .map(([playerId]) => playerId)

    // Build equal contributions for this pot
    const contributions: Record<string, number> = {}
    for (const playerId of contributingPlayerIds) {
      contributions[playerId] = amountPerPlayer
    }

    // Eligible players are those who contributed AND are not folded
    const eligiblePlayerIds = contributingPlayerIds.filter((id) => eligibleSet.has(id))

    pots.push({
      amount: amountPerPlayer * contributingPlayerIds.length,
      eligiblePlayerIds,
      contributions,
      cumulativeLevel: level
    })

    previousLevel = level
  }

  return pots
}

/**
 * Merge consecutive pots that have identical eligible player sets.
 *
 * This handles the case where a folded player's contribution (e.g. a blind)
 * creates a pot boundary at their bet level, but since they're not eligible,
 * the resulting pot has the same eligible players as the next pot. Displaying
 * two pots with the same eligible players is confusing in the UI and
 * unnecessary — merging them is safe because distribution outcomes are
 * identical when the eligible set is the same.
 *
 * @internal
 */
function mergePotsWithSameEligibility(pots: Pot[]): Pot[] {
  if (pots.length <= 1) return pots

  const merged: Pot[] = []
  let current = { ...pots[0] }

  for (let i = 1; i < pots.length; i++) {
    const next = pots[i]

    const sameEligible =
      current.eligiblePlayerIds.length === next.eligiblePlayerIds.length &&
      current.eligiblePlayerIds.every((id) => next.eligiblePlayerIds.includes(id))

    if (sameEligible) {
      const mergedContributions = { ...current.contributions }
      for (const [playerId, amount] of Object.entries(next.contributions)) {
        mergedContributions[playerId] = (mergedContributions[playerId] || 0) + amount
      }

      current = {
        amount: current.amount + next.amount,
        eligiblePlayerIds: current.eligiblePlayerIds,
        contributions: mergedContributions,
        cumulativeLevel: next.cumulativeLevel
      }
    } else {
      merged.push(current)
      current = { ...next }
    }
  }

  merged.push(current)
  return merged
}

// ============================================================================
// CORE FUNCTIONS - Main sidepot calculation logic
// ============================================================================

/**
 * Calculate pots from player bets
 *
 * In poker, side pots are ONLY created when a player goes all-in. When a player
 * cannot match another player's bet because they don't have enough chips, a side pot
 * is created for the additional chips that only the players who can afford it compete for.
 *
 * Algorithm:
 * 1. Filter to players with bets (currentBet > 0)
 * 2. Identify all-in players (state === 'allin')
 * 3. If NO all-ins exist, create single main pot with all chips
 * 4. If all-ins exist, create pots based on all-in amounts:
 *    - Main pot: smallest all-in amount × all contributing players
 *    - Side pots: created for each higher all-in or bet level
 * 5. Track which players are eligible for each pot (non-folded only)
 *
 * @example
 * ```typescript
 * // Example 1: No all-ins - single pot only
 * const players = [
 *   { id: 'p1', currentBet: 100, state: 'active', ... },
 *   { id: 'p2', currentBet: 200, state: 'active', ... }
 * ]
 * // Result: [{ amount: 300, cumulativeLevel: 0, ... }]
 *
 * // Example 2: With all-in - creates multiple pots
 * const players = [
 *   { id: 'p1', currentBet: 100, state: 'allin', ... },
 *   { id: 'p2', currentBet: 200, state: 'active', ... }
 * ]
 * // Result: [
 * //   { amount: 200, cumulativeLevel: 100, ... },  // Main pot
 * //   { amount: 100, cumulativeLevel: 200, ... }   // Side pot
 * // ]
 * ```
 *
 * @param players - All players with currentBet amounts
 * @returns PotStructure (Pot[]) sorted by cumulativeLevel
 */
export function calculateSidepots(players: EnginePlayer[]): PotStructure {
  // Filter players who have contributed to the pot (currentBet > 0)
  const playersWithBets = players.filter((p) => p.currentBet > 0)

  // If no bets, return empty pot structure
  if (playersWithBets.length === 0) {
    return createEmptyPotStructure()
  }

  // Check if any player is all-in (side pots only form with all-ins)
  const allInPlayers = playersWithBets.filter((p) => p.state === 'allin')

  // If NO all-ins, create a single main pot with all chips
  if (allInPlayers.length === 0) {
    const totalAmount = playersWithBets.reduce((sum, p) => sum + p.currentBet, 0)
    // `sittingout` is filtered alongside `folded`. A sittingout player whose
    // blind was force-posted (`BlindPolicy: 'force'`) contributes chips to
    // the pot as dead money but isn't in the hand — they can't win at
    // showdown. The engine's `findWinners` also defensively filters on
    // `cards.length > 0`, but the eligibility list shouldn't carry them at
    // all so downstream consumers (UI banners, hand-history) read clean.
    const eligiblePlayers = playersWithBets.filter((p) => p.state !== 'folded' && p.state !== 'sittingout')
    const contributions: Record<string, number> = {}

    for (const player of playersWithBets) {
      contributions[player.id] = player.currentBet
    }

    return createSinglePot(totalAmount, eligiblePlayers.map((p) => p.id), contributions)
  }

  // At least one all-in exists - create pot structure based on bet levels
  const sortedPlayers = [...playersWithBets].sort((a, b) => a.currentBet - b.currentBet)

  // Get unique bet amounts (these will be our "levels")
  const betLevels: number[] = []
  for (const player of sortedPlayers) {
    if (!betLevels.includes(player.currentBet)) {
      betLevels.push(player.currentBet)
    }
  }

  // Build pots from each bet level
  const pots: Pot[] = []
  let previousLevel = 0

  for (const level of betLevels) {
    const amountPerPlayer = level - previousLevel

    // All players who bet at least this level contribute to this pot
    const contributingPlayers = playersWithBets.filter((p) => p.currentBet >= level)

    // Only non-folded, non-sittingout players are eligible to WIN. A
    // sittingout player whose blind was force-posted contributed chips
    // (dead money) but isn't in the hand. See `findWinners` for the
    // defensive `cards.length > 0` check that catches any path that
    // bypasses this filter.
    const eligiblePlayers = contributingPlayers.filter((p) => p.state !== 'folded' && p.state !== 'sittingout')

    const potAmount = contributingPlayers.length * amountPerPlayer
    const contributions: Record<string, number> = {}

    for (const player of contributingPlayers) {
      contributions[player.id] = amountPerPlayer
    }

    pots.push({
      amount: potAmount,
      eligiblePlayerIds: eligiblePlayers.map((p) => p.id),
      contributions,
      cumulativeLevel: level
    })

    previousLevel = level
  }

  const result = pots.length > 0 ? pots : createEmptyPotStructure()
  return mergePotsWithSameEligibility(result)
}

/**
 * Merge pot structures from multiple betting rounds
 *
 * In real poker, chips accumulate in pots across betting rounds. When a new
 * betting round completes, the chips from that round are added to the existing
 * pot structure.
 *
 * CRITICAL POKER RULE: Each pot must have EQUAL contributions from all participants.
 * Side pots are ONLY created when a player goes all-in. This function rebuilds
 * the pot structure based on all-in levels, ensuring equal contributions per pot.
 *
 * @param existingPots - Pot structure from previous betting rounds
 * @param newRoundPots - Pot structure calculated from current round's bets
 * @param players - Optional player array to determine all-in states
 * @returns Merged pot structure (Pot[]) with equal contributions per pot
 */
export function mergePotStructures(
  existingPots: PotStructure,
  newRoundPots: PotStructure,
  players?: EnginePlayer[]
): PotStructure {
  // Step 1: Calculate cumulative contributions per player across all pots
  const cumulativeContributions: Record<string, number> = {}
  collectContributionsFromStructure(existingPots, cumulativeContributions)
  collectContributionsFromStructure(newRoundPots, cumulativeContributions)

  // If no contributions, return empty structure
  if (Object.keys(cumulativeContributions).length === 0) {
    return createEmptyPotStructure()
  }

  // Step 2: Determine all-in players and their contribution levels
  const { allInLevels } = determineAllInLevels(players, cumulativeContributions, existingPots, newRoundPots)

  // Step 3: Build eligibility set
  const eligibleSet = buildEligibilitySet(players, newRoundPots, existingPots, cumulativeContributions)

  // Step 4: Build pot structure
  // If no all-ins, create single main pot with all chips
  if (allInLevels.size === 0) {
    const totalAmount = Object.values(cumulativeContributions).reduce((sum, c) => sum + c, 0)
    const eligiblePlayerIds = Object.keys(cumulativeContributions).filter((id) => eligibleSet.has(id))

    return createSinglePot(totalAmount, eligiblePlayerIds, { ...cumulativeContributions })
  }

  // With all-ins: create pots at each unique contribution level
  const allContributionLevels = [...new Set(Object.values(cumulativeContributions))].sort((a, b) => a - b)
  const pots = buildPotsFromLevels(allContributionLevels, cumulativeContributions, eligibleSet)

  const result = pots.length > 0 ? pots : createEmptyPotStructure()
  return mergePotsWithSameEligibility(result)
}

// ============================================================================
// QUERY FUNCTIONS - Read information from pot structures
// ============================================================================

/**
 * Get eligible players for a specific pot
 * Filters the full player list to only those eligible to win this pot
 *
 * @param pot - The pot to check eligibility for
 * @param allPlayers - All players in the game
 * @returns Array of players eligible to win from this pot
 */
export function getEligiblePlayers(pot: Pot, allPlayers: EnginePlayer[]): EnginePlayer[] {
  return allPlayers.filter((player) => pot.eligiblePlayerIds.includes(player.id))
}

/**
 * Calculate total pot amount across all pots
 * Sums all pots in the structure
 *
 * @param potStructure - The pot structure (Pot[])
 * @returns Total chips across all pots
 */
export function getTotalPotAmount(potStructure: PotStructure): number {
  return potStructure.reduce((sum, pot) => sum + pot.amount, 0)
}

// ============================================================================
// DISTRIBUTION FUNCTIONS - Award pots to winners
// ============================================================================

/**
 * Distribute all pots to winners based on hand strength and eligibility
 *
 * Evaluates each pot separately, determining winners only from players eligible
 * for that specific pot. This ensures correct chip distribution in multi-way all-in
 * scenarios where different players are eligible for different pots.
 *
 * @param potStructure - The pot structure to distribute (Pot[])
 * @param players - All players in the hand
 * @param communityCards - Board cards for hand evaluation
 * @returns Complete distribution showing winners and amounts per pot
 */
export function distributePots(
  potStructure: PotStructure,
  players: EnginePlayer[],
  communityCards: Card[]
): PotDistribution {
  const potWinners = []
  let totalAwarded = 0

  for (let potIndex = 0; potIndex < potStructure.length; potIndex++) {
    const pot = potStructure[potIndex]

    // Find winners for this pot (only eligible players)
    const winners = findWinnersForPot(pot, players, communityCards)

    if (winners.length === 0) {
      // No eligible winners (all folded) - pot remains (edge case)
      continue
    }

    // Distribute pot equally among winners
    const amountPerWinner = Math.floor(pot.amount / winners.length)
    const remainder = pot.amount % winners.length

    potWinners.push({
      potIndex,
      winners: winners.map((winner, index) => ({
        playerId: winner.playerId,
        userId: winner.userId,
        playerName: winner.playerName,
        amount: amountPerWinner + (index === 0 ? remainder : 0),
        hand: winner.hand
      }))
    })

    totalAwarded += pot.amount
  }

  return {
    totalAwarded,
    potWinners
  }
}

// ============================================================================
// FORMATTING FUNCTIONS - Debug and display utilities
// ============================================================================

/**
 * Format pot structure as human-readable string for debugging or UI display
 *
 * @param potStructure - The pot structure to format (Pot[])
 * @returns Human-readable string describing all pots
 */
export function formatPotStructure(potStructure: PotStructure): string {
  if (potStructure.length === 0) {
    return 'No pots'
  }

  const parts: string[] = []

  potStructure.forEach((pot, index) => {
    const potName = index === 0 ? 'Main Pot' : `Side Pot #${index}`
    parts.push(`${potName}: $${pot.amount} (${pot.eligiblePlayerIds.length} players, level: ${pot.cumulativeLevel})`)
  })

  return parts.join(', ')
}

/**
 * Format pot structure with detailed contribution breakdown
 * Useful for debugging complex multi-way all-in scenarios
 *
 * @param potStructure - The pot structure to format (Pot[])
 * @returns Detailed multi-line string with all contribution information
 */
export function formatPotStructureDetailed(potStructure: PotStructure): string {
  const lines: string[] = []

  potStructure.forEach((pot, index) => {
    const potName = index === 0 ? 'Main Pot' : `Side Pot #${index}`
    lines.push(`${potName}: $${pot.amount} (cumulativeLevel: ${pot.cumulativeLevel})`)
    lines.push(`  Eligible: ${pot.eligiblePlayerIds.join(', ') || '(none)'}`)

    const contribParts = Object.entries(pot.contributions)
      .map(([id, amount]) => `${id}: $${amount}`)
      .join(', ')
    lines.push(`  Contributions: ${contribParts || '(none)'}`)
  })

  const cumulative = getCumulativeContributions(potStructure)
  lines.push('')
  lines.push('Cumulative Contributions:')
  for (const [playerId, total] of cumulative) {
    lines.push(`  ${playerId}: $${total}`)
  }
  lines.push(`Total in pots: $${getTotalPotAmount(potStructure)}`)

  return lines.join('\n')
}

// ============================================================================
// BACKWARD COMPATIBILITY - Convert between old and new formats
// ============================================================================

/**
 * Convert old {mainPot, sidePots} format to new Pot[] format
 * Use this when migrating from legacy code
 *
 * @deprecated This is for migration only. Use Pot[] directly.
 */
export function fromLegacyPotStructure(legacy: {
  mainPot: Omit<Pot, 'cumulativeLevel'> & { cumulativeLevel?: number }
  sidePots: Array<Omit<Pot, 'cumulativeLevel'> & { cumulativeLevel?: number }>
}): PotStructure {
  const pots: Pot[] = []

  // Main pot gets cumulativeLevel 0 if not set
  pots.push({
    ...legacy.mainPot,
    cumulativeLevel: legacy.mainPot.cumulativeLevel ?? 0
  })

  // Side pots get increasing levels if not set
  legacy.sidePots.forEach((sidePot, index) => {
    pots.push({
      ...sidePot,
      cumulativeLevel: sidePot.cumulativeLevel ?? (index + 1) * 100 // Placeholder levels
    })
  })

  return pots
}

/**
 * Convert new Pot[] format to old {mainPot, sidePots} format
 * Use this when interfacing with legacy code
 *
 * @deprecated This is for migration only. Prefer Pot[] directly.
 */
export function toLegacyPotStructure(pots: PotStructure): {
  mainPot: Pot
  sidePots: Pot[]
} {
  if (pots.length === 0) {
    return {
      mainPot: { amount: 0, eligiblePlayerIds: [], contributions: {}, cumulativeLevel: 0 },
      sidePots: []
    }
  }

  const [mainPot, ...sidePots] = pots
  return { mainPot, sidePots }
}
